import { z } from "zod";

import type { AuditChange } from "./organizations";
import {
	describeNewParty,
	describePartyChanges,
	endDateAfterSince,
	endDateNotBeforeSince,
	filledPartyText,
	missingPartyFields,
	optionalPartyText,
	PARTY_CONTACT_AUDIT_FIELDS,
	partyAddedAudit,
	partyContactFields,
	partyContactFrom,
	partyContactRecordFields,
	partyCurrencySchema,
	partyDateField,
	partyFieldsFrom,
	partyRecordFields,
	partyRemovedAudit,
	partyRequestFields,
	partyUpdatedAudit,
	type PartyAudit,
	type PartyField,
	type PartyRequirement,
} from "./parties";
import { PAYMENT_TERM_LABELS, paymentTermsSchema } from "./payment-terms";

// Vendors: the suppliers the business buys from, and the terms it buys on. What a vendor shares with every other kind
// of party — its name, its dates, its note, whom to contact and how a change reads in the audit log — comes from
// `./parties.ts`; what is here is the commercial shape that makes it a supplier. The API and the frontend share these.

/** What kind of supplier this is. A classification the app offers, not a spelling the organization invents. */
export const VENDOR_CATEGORIES = [
	"software",
	"professional-services",
	"hardware",
	"logistics",
	"facilities",
	"marketing",
	"financial",
	"utilities",
	"other",
] as const;
export type VendorCategory = (typeof VENDOR_CATEGORIES)[number];

export const VENDOR_CATEGORY_LABELS: Readonly<Record<VendorCategory, string>> = {
	software: "Software & SaaS",
	"professional-services": "Professional services",
	hardware: "Hardware & equipment",
	logistics: "Logistics & shipping",
	facilities: "Facilities & office",
	marketing: "Marketing & advertising",
	financial: "Financial services",
	utilities: "Utilities",
	other: "Other",
};

/**
 * What a vendor supplies and on what terms: the three choices a person's request cannot do without, each one a value
 * out of a fixed list, refused in the words the form shows while none is chosen.
 */
const vendorTermsFields = {
	category: z.enum(VENDOR_CATEGORIES, "Choose a category"),
	paymentTerms: paymentTermsSchema,
	/** What they invoice in, which need not be the company's own reporting currency. */
	currency: partyCurrencySchema,
};

/**
 * A vendor as the vendors table keeps it. The three choices are optional here and nowhere else: a person's request
 * always carries them, and only the Directory agent adds a vendor without them (`VENDOR_REQUIREMENTS`).
 */
export const vendorSchema = z.object({
	...partyRecordFields,
	...partyContactRecordFields,
	category: vendorTermsFields.category.optional(),
	paymentTerms: vendorTermsFields.paymentTerms.optional(),
	currency: vendorTermsFields.currency.optional(),
	/** The entity on the contract, when it is not what the business calls them. */
	legalName: optionalPartyText(160).optional(),
	/** VAT or tax registration number, as it appears on their invoices. */
	taxId: optionalPartyText(40).optional(),
});
export type Vendor = z.infer<typeof vendorSchema>;

/**
 * The fields of a vendor that a request carries, trimmed as the form sends them. The two optional text fields accept
 * "" because the form holds a field nobody filled in as an empty string, exactly as the contact block does. The form
 * spreads this rather than naming any of them again, so the two refuse the same thing in the same words and cap the
 * same lengths; only the fields an input owns — the dates it hands over as "" and the note the editor always writes —
 * are the form's own.
 */
export const vendorRequestFields = {
	...partyRequestFields,
	...partyContactFields,
	...vendorTermsFields,
	legalName: optionalPartyText(160),
	taxId: optionalPartyText(40),
};

const vendorFieldsSchema = z.object(vendorRequestFields);

/**
 * What the form sends to add or to change a vendor. Add and change carry the same fields: unlike a person, there is
 * nothing the API owns that a client could send by mistake, and the note is written from the first save.
 */
export const vendorRequestSchema = vendorFieldsSchema.refine(endDateAfterSince, endDateNotBeforeSince);
export type VendorRequest = z.infer<typeof vendorRequestSchema>;

/**
 * The record an add or a change leaves behind: the id and the day it was added are the API's, and everything else is
 * what the request carried — a field somebody left empty left out rather than kept as "", and a note of nothing but
 * spaces no note at all. The two write handlers and the frontend's fake API all build a record this way, so none of
 * the three can drift from the others.
 */
export function vendorFrom(id: string, addedAt: string, request: VendorRequest): Vendor {
	const { legalName, taxId } = request;
	return {
		id,
		...partyFieldsFrom(request),
		...partyContactFrom(request),
		...filledPartyText({ legalName, taxId }),
		category: request.category,
		paymentTerms: request.paymentTerms,
		currency: request.currency,
		addedAt,
	};
}

/**
 * What a person must fill in before a vendor is complete, in form order and under the form's labels. A person's request
 * always carries all four; the Directory agent may add a vendor with none of them, and the Directory shows that vendor
 * as incomplete until somebody fills them in.
 */
export const VENDOR_REQUIREMENTS: readonly PartyRequirement<Vendor>[] = [
	["category", "Category"],
	["paymentTerms", "Payment terms"],
	["currency", "Currency"],
	["since", "Start date"],
];

/** The labels of what a vendor still lacks, in form order: none once somebody has filled it in. */
export function missingVendorFields(vendor: Vendor): string[] {
	return missingPartyFields(vendor, VENDOR_REQUIREMENTS);
}

/** The line under a vendor's name: what it supplies, by label, or that nobody has said yet. */
export function vendorDetail(vendor: Vendor): string {
	return vendor.category === undefined ? "No category yet" : VENDOR_CATEGORY_LABELS[vendor.category];
}

/**
 * Every field of a vendor the audit log names, in the order an entry lists them — a category and a payment term by
 * their label, never by the id the taxonomy keeps them under. The note is not among them: `describeNewParty` and
 * `describePartyChanges` report what became of it and never a word of what it says.
 */
const VENDOR_FIELDS: readonly PartyField<Vendor>[] = [
	["Name", vendor => vendor.name],
	["Legal name", vendor => vendor.legalName],
	["Category", vendor => (vendor.category === undefined ? undefined : VENDOR_CATEGORY_LABELS[vendor.category])],
	...PARTY_CONTACT_AUDIT_FIELDS,
	["Tax ID", vendor => vendor.taxId],
	[
		"Payment terms",
		vendor => (vendor.paymentTerms === undefined ? undefined : PAYMENT_TERM_LABELS[vendor.paymentTerms]),
	],
	["Currency", vendor => vendor.currency],
	partyDateField("Start date", vendor => vendor.since),
	partyDateField("End date", vendor => vendor.endDate),
];

/**
 * A new vendor's values: every field it was added with, and nothing before it. An optional field nobody filled in
 * reads as `UNSPECIFIED_VALUE`, and a date nobody gave as `UNSPECIFIED_DATE`, the way `describeNewOtherParty` does.
 */
export function describeNewVendor(vendor: Vendor): AuditChange[] {
	return describeNewParty(vendor, VENDOR_FIELDS);
}

/** What changed between two versions of a vendor, newest value first, for the audit log. */
export function describeVendorChanges(before: Vendor, after: Vendor): AuditChange[] {
	return describePartyChanges(before, after, VENDOR_FIELDS);
}

/** The audit entry for a vendor added to the Directory: "Added party" / "Northwind Systems · Software & SaaS". */
export function vendorAddedAudit(vendor: Vendor): PartyAudit {
	return partyAddedAudit(vendor.name, vendorDetail(vendor));
}

/** The audit entry for a vendor whose record changed: "Updated party" / "Northwind Systems · Payment terms: Net 45". */
export function vendorUpdatedAudit(vendor: Vendor, changes: readonly AuditChange[]): PartyAudit {
	return partyUpdatedAudit(vendor.name, changes);
}

/** The audit entry for a vendor taken out of the Directory: "Removed party" / "Northwind Systems · Software & SaaS". */
export function vendorRemovedAudit(vendor: Vendor): PartyAudit {
	return partyRemovedAudit(vendor.name, vendorDetail(vendor));
}
